import React, { useState, useEffect, useCallback, useRef } from 'react';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import VendorProductDetail from './VendorProductDetail';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  searchBar: {
    marginBottom: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filterControl: {
    minWidth: 180,
  },
  tableContainer: {
    marginTop: theme.spacing(1),
  },
  skuCell: {
    fontFamily: 'monospace',
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  productThumb: {
    width: 44,
    height: 44,
    objectFit: 'contain',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: '#f5f5f5',
    flexShrink: 0,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  badge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    height: 20,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  confidenceChip: {
    color: '#fff',
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
  },
  mappingChip: {
    color: '#fff',
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
  },
  liveSectionDivider: {
    margin: theme.spacing(4, 0, 2),
  },
  liveSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  liveChip: {
    backgroundColor: '#1565c0',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.65rem',
    height: 20,
    letterSpacing: '0.05em',
  },
  liveGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(2),
  },
  liveCard: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  liveCardImage: {
    width: '100%',
    height: 140,
    objectFit: 'contain',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(1),
  },
  liveCardImagePlaceholder: {
    width: '100%',
    height: 140,
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.disabled,
    fontSize: '0.75rem',
  },
  liveCardBody: {
    padding: theme.spacing(1.5),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  liveCardName: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.3,
  },
  liveCardMeta: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  liveCardCategory: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  liveCardPrice: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
  },
  liveCardActions: {
    padding: theme.spacing(1, 1.5, 1.5),
  },
  hydrateButton: {
    width: '100%',
    position: 'relative',
  },
  hydrateButtonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -10,
    marginLeft: -10,
  },
}));

const MAPPING_STATUS_COLORS = {
  mapped: '#4caf50',
  suggested: '#2196f3',
  unmapped: '#9e9e9e',
  ignored: '#757575',
};

const MAPPING_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'mapped', label: 'Mapped' },
  { value: 'unmapped', label: 'Unmapped' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'ignored', label: 'Ignored' },
];

function getConfidenceColor(confidence) {
  if (confidence >= 0.85) return '#4caf50';
  if (confidence >= 0.5) return '#ff9800';
  return '#f44336';
}

function descendingComparator(a, b, orderBy) {
  const valA = a[orderBy] ?? '';
  const valB = b[orderBy] ?? '';
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

const VendorCatalogSearch = () => {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [mappingStatus, setMappingStatus] = useState('all');
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('productName');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [liveResults, setLiveResults] = useState([]);
  const [hydratingKeys, setHydratingKeys] = useState({});
  const debounceRef = useRef(null);

  // Fetch categories on mount
  useEffect(() => {
    fetch('/api/vendor-catalogs/products/categories')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setCategories(data);
      })
      .catch(() => {});
  }, []);

  const executeSearch = useCallback(
    (searchQuery, searchCategory, searchMapping, searchPage, searchLimit) => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults([]);
        setTotal(0);
        return;
      }
      setLoading(true);
      const params = new URLSearchParams({
        q: searchQuery,
        limit: searchLimit,
        skip: searchPage * searchLimit,
      });
      if (searchCategory) params.append('category', searchCategory);
      if (searchMapping && searchMapping !== 'all') params.append('mappingStatus', searchMapping);

      fetch(`/api/vendor-catalogs/products/search?${params.toString()}`)
        .then((res) => res.json())
        .then((data) => {
          setResults(data.results || []);
          setTotal(data.total || 0);
          setLiveResults(data.liveResults || []);
          setLoading(false);
        })
        .catch(() => {
          setResults([]);
          setTotal(0);
          setLiveResults([]);
          setLoading(false);
        });
    },
    []
  );

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      executeSearch(query, category, mappingStatus, 0, rowsPerPage);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, category, mappingStatus, rowsPerPage, executeSearch]);

  // Re-search on page change (not debounced)
  useEffect(() => {
    if (query.length >= 2) {
      executeSearch(query, category, mappingStatus, page, rowsPerPage);
    }
  }, [page]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRowClick = (product) => {
    setSelectedProductId(product._id);
    setDrawerOpen(true);
  };

  const handleHydrate = useCallback(
    (urlKey) => {
      setHydratingKeys((prev) => ({ ...prev, [urlKey]: true }));
      fetch('/api/vendor-catalogs/ewing/hydrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urlKey }),
      })
        .then((res) => res.json())
        .then(() => {
          setHydratingKeys((prev) => ({ ...prev, [urlKey]: false }));
          // Re-run the current search to pick up the newly hydrated product
          executeSearch(query, category, mappingStatus, page, rowsPerPage);
        })
        .catch(() => {
          setHydratingKeys((prev) => ({ ...prev, [urlKey]: false }));
        });
    },
    [query, category, mappingStatus, page, rowsPerPage, executeSearch]
  );

  const sortedResults = stableSort(results, getComparator(order, orderBy));

  const sortableColumns = [
    { id: 'productName', label: 'Product' },
    { id: 'vendorItemNumber', label: 'SKU' },
    { id: 'category', label: 'Category' },
    { id: 'pageNumber', label: 'Page' },
    { id: 'confidence', label: 'Confidence' },
  ];

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        Vendor Catalog Search
      </Typography>

      {/* Search bar */}
      <TextField
        className={classes.searchBar}
        fullWidth
        variant="outlined"
        placeholder="Search products by name, SKU, or description..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Filter row */}
      <div className={classes.filterRow}>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Mapping Status</InputLabel>
          <Select
            value={mappingStatus}
            onChange={(e) => setMappingStatus(e.target.value)}
            label="Mapping Status"
          >
            {MAPPING_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Results */}
      {query.length < 2 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">Type at least 2 characters to search</Typography>
        </div>
      ) : results.length === 0 && !loading ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">No products found</Typography>
        </div>
      ) : (
        <Paper>
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {sortableColumns.map((col) => (
                    <TableCell key={col.id}>
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Mapping</TableCell>
                  <TableCell>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedResults.map((product) => (
                  <TableRow
                    key={product._id}
                    className={classes.clickableRow}
                    onClick={() => handleRowClick(product)}
                  >
                    <TableCell>
                      <div className={classes.productCell}>
                        {product.imageUrl && (
                          <img
                            src={product.imageUrl}
                            alt={product.productName}
                            className={classes.productThumb}
                          />
                        )}
                        <span>{product.productName}</span>
                      </div>
                    </TableCell>
                    <TableCell className={classes.skuCell}>
                      {product.vendorItemNumber || '-'}
                    </TableCell>
                    <TableCell>{product.category || '-'}</TableCell>
                    <TableCell>{product.pageNumber ?? '-'}</TableCell>
                    <TableCell>
                      {product.confidence != null ? (
                        <Chip
                          label={`${Math.round(product.confidence * 100)}%`}
                          size="small"
                          className={classes.confidenceChip}
                          style={{
                            backgroundColor: getConfidenceColor(product.confidence),
                          }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{product.manufacturer || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={product.mappingStatusSummary || 'unmapped'}
                        size="small"
                        className={classes.mappingChip}
                        style={{
                          backgroundColor:
                            MAPPING_STATUS_COLORS[product.mappingStatusSummary] || '#9e9e9e',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {product._matchType === 'exact_sku' && (
                        <Chip
                          label="Exact"
                          size="small"
                          className={classes.badge}
                          style={{ backgroundColor: '#4caf50', color: '#fff' }}
                        />
                      )}
                      {product._matchType === 'prefix_sku' && (
                        <Chip
                          label="Prefix"
                          size="small"
                          className={classes.badge}
                          style={{ backgroundColor: '#2196f3', color: '#fff' }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > rowsPerPage && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onChangePage={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          )}
        </Paper>
      )}

      {/* Live results from Ewing */}
      {liveResults.length > 0 && (
        <>
          <Divider className={classes.liveSectionDivider} />
          <div className={classes.liveSectionHeader}>
            <Typography variant="h6">From Ewing</Typography>
            <Chip
              label="LIVE"
              size="small"
              className={classes.liveChip}
            />
            <Typography variant="body2" color="textSecondary">
              {liveResults.length} result{liveResults.length !== 1 ? 's' : ''} not yet in local catalog
            </Typography>
          </div>
          <div className={classes.liveGrid}>
            {liveResults.map((product) => {
              const isHydrating = !!hydratingKeys[product.urlKey];
              const price = product.pricing && (product.pricing.accountPrice || product.pricing.regularPrice);
              return (
                <div key={product.urlKey || product.vendorItemNumber} className={classes.liveCard}>
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.productName}
                      className={classes.liveCardImage}
                    />
                  ) : (
                    <div className={classes.liveCardImagePlaceholder}>No image</div>
                  )}
                  <div className={classes.liveCardBody}>
                    <Typography className={classes.liveCardName}>
                      {product.productName}
                    </Typography>
                    {product.vendorItemNumber && (
                      <Typography className={classes.liveCardMeta}>
                        SKU: {product.vendorItemNumber}
                      </Typography>
                    )}
                    {product.category && (
                      <Typography className={classes.liveCardCategory}>
                        {product.category}
                      </Typography>
                    )}
                    {price != null && (
                      <Typography className={classes.liveCardPrice}>
                        ${typeof price === 'number' ? price.toFixed(2) : price}
                      </Typography>
                    )}
                  </div>
                  <div className={classes.liveCardActions}>
                    <div style={{ position: 'relative' }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={classes.hydrateButton}
                        disabled={isHydrating}
                        onClick={() => handleHydrate(product.urlKey)}
                      >
                        {isHydrating ? 'Adding...' : 'Add to Catalog'}
                      </Button>
                      {isHydrating && (
                        <CircularProgress
                          size={20}
                          className={classes.hydrateButtonProgress}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Product Detail Drawer */}
      <VendorProductDetail
        productId={selectedProductId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
};

export default VendorCatalogSearch;
